import React from 'react';
import { Card, Button, ContactForm } from '../ui';
import { firmData } from '../../data/firmData';
import type { Office } from '../../types/firm';

export const ContactSection: React.FC = () => {
  // Filter to show only Chennai office
  const chennaiOffice = firmData.offices.find(office => office.city === 'Chennai');

  return (
    <section id="contact" className="py-16 bg-background-white">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="heading-lg text-primary-navy mb-4">
            Contact Information
          </h2>
          <p className="body-lg text-text-medium max-w-3xl mx-auto mb-8">
            Get in touch with our legal team for professional consultation and expert guidance 
            across our comprehensive range of legal services.
          </p>
          
          {/* Important Legal Disclaimer */}
          <div className="bg-accent-gold/5 border border-accent-gold/20 rounded-lg p-6 max-w-4xl mx-auto">
            <h3 className="heading-sm text-primary-navy mb-3">
              Important Legal Notice
            </h3>
            <p className="body-sm text-text-medium leading-relaxed">
              <strong>No Attorney-Client Relationship:</strong> Contacting VC Law Associates through this website, 
              by email, or by phone does not create an attorney-client relationship. Please do not send any 
              confidential information until such a relationship has been established through a formal engagement letter. 
              Any information transmitted through this website is not guaranteed to be secure or confidential.
            </p>
          </div>
        </div>

        {/* Reach Us — consolidated office + contact methods */}
        <div className="mb-16">
          <h3 className="heading-md text-primary-navy mb-8 text-center">Reach Us</h3>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Office Location */}
            {chennaiOffice && <OfficeCard office={chennaiOffice} isHeadOffice={false} />}

            {/* Contact Methods */}
            <Card variant="elevated" className="p-6">
              <h4 className="heading-sm text-primary-navy mb-6 text-center">Get in Touch</h4>
              <ul className="space-y-5">
                {chennaiOffice?.phone && (
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-navy rounded-full flex items-center justify-center flex-shrink-0" role="img" aria-label="Phone">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </div>
                    <div>
                      <div className="body-sm text-text-medium">Phone</div>
                      <a
                        href={`tel:${chennaiOffice.phone.replace(/\s+/g, '')}`}
                        className="body-base text-primary-navy font-medium hover:text-accent-gold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:ring-offset-2 rounded"
                        aria-label={`Call us at ${chennaiOffice.phone}`}
                      >
                        {chennaiOffice.phone}
                      </a>
                    </div>
                  </li>
                )}

                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-navy rounded-full flex items-center justify-center flex-shrink-0" role="img" aria-label="WhatsApp">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M10 0C4.477 0 0 4.477 0 10c0 1.89.525 3.66 1.438 5.168L0 20l4.988-1.392A9.956 9.956 0 0010 20c5.523 0 10-4.477 10-10S15.523 0 10 0zm5.805 14.176c-.241.678-1.198 1.242-1.965 1.405-.525.109-1.213.196-3.527-.757-2.967-1.221-4.883-4.217-5.032-4.41-.144-.194-1.176-1.566-1.176-2.986 0-1.42.744-2.118 1.008-2.407.264-.289.576-.362.768-.362.192 0 .385.002.553.01.177.009.414-.067.648.494.24.576.816 1.995.888 2.14.072.145.12.314.024.507-.096.194-.144.314-.288.483-.144.169-.303.377-.433.506-.144.145-.294.301-.126.59.168.288.747 1.233 1.604 1.998 1.102.985 2.031 1.291 2.319 1.436.288.145.457.121.625-.072.168-.194.72-.842.913-1.13.192-.289.385-.241.649-.145.264.097 1.677.791 1.965.935.288.145.481.217.553.337.072.121.072.698-.169 1.376z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="body-sm text-text-medium">WhatsApp</div>
                    <p className="body-base text-primary-navy font-medium">+91 91766 73939</p>
                  </div>
                </li>

                {chennaiOffice?.email && (
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-navy rounded-full flex items-center justify-center flex-shrink-0" role="img" aria-label="Email">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <div className="body-sm text-text-medium">Email</div>
                      <a
                        href={`mailto:${chennaiOffice.email}`}
                        className="body-base text-primary-navy font-medium hover:text-accent-gold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:ring-offset-2 rounded break-all"
                        aria-label={`Send email to ${chennaiOffice.email}`}
                      >
                        {chennaiOffice.email}
                      </a>
                    </div>
                  </li>
                )}
              </ul>
            </Card>
          </div>
        </div>

        {/* Contact Form */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto">
            <Card variant="elevated" className="p-8">
              <ContactForm />
            </Card>
          </div>
        </div>

        {/* Professional Consultation Notice */}
        <div className="text-center">
          <div className="bg-background-light border border-border-subtle rounded-lg p-8 max-w-4xl mx-auto">
            <h4 className="heading-sm text-primary-navy mb-4">
              Professional Legal Consultation
            </h4>
            <p className="body-base text-text-medium leading-relaxed mb-6">
              Our experienced legal team is available to discuss your specific legal requirements and provide 
              professional guidance tailored to your situation. We maintain strict confidentiality and 
              professional standards in all our client interactions.
            </p>
            
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 text-left">
              <div>
                <h5 className="body-base font-semibold text-primary-navy mb-2">
                  Initial Consultation Process:
                </h5>
                <ul className="body-sm text-text-medium space-y-1">
                  <li>• Preliminary case assessment</li>
                  <li>• Discussion of legal options</li>
                  <li>• Fee structure explanation</li>
                  <li>• Engagement terms review</li>
                </ul>
              </div>
              
              <div>
                <h5 className="body-base font-semibold text-primary-navy mb-2">
                  What to Prepare:
                </h5>
                <ul className="body-sm text-text-medium space-y-1">
                  <li>• Relevant documents and records</li>
                  <li>• Timeline of events</li>
                  <li>• Specific questions or concerns</li>
                  <li>• Preferred consultation method</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8">
              <Button variant="primary" size="lg">
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

interface OfficeCardProps {
  office: Office;
  isHeadOffice: boolean;
}

const OfficeCard: React.FC<OfficeCardProps> = ({ office, isHeadOffice }) => {
  return (
    <Card variant="elevated" className="text-center">
      <div className="flex items-center justify-center mb-4">
        <div 
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isHeadOffice ? 'bg-accent-gold' : 'bg-primary-navy'
          }`}
          role="img"
          aria-label={`${office.city} office location`}
        >
          <svg 
            className={`w-6 h-6 ${isHeadOffice ? 'text-primary-navy' : 'text-white'}`} 
            fill="currentColor" 
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        </div>
        {isHeadOffice && (
          <span className="ml-3 bg-accent-gold/10 text-accent-gold px-3 py-1 rounded-full body-sm font-medium">
            Head Office
          </span>
        )}
      </div>
      
      <h5 className="heading-sm text-primary-navy mb-4">{office.city}</h5>
      
      <address className="space-y-2 text-left not-italic">
        <div className="flex items-start gap-3">
          <svg 
            className="w-5 h-5 text-text-light mt-0.5 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <div className="body-sm text-text-medium">
            <p>{office.address}</p>
            <p>{office.city}, {office.state} {office.pinCode}</p>
          </div>
        </div>
        
        {office.phone && (
          <div className="flex items-center gap-3">
            <svg 
              className="w-5 h-5 text-text-light flex-shrink-0" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            <a 
              href={`tel:${office.phone.replace(/\s+/g, '')}`}
              className="body-sm text-text-medium hover:text-primary-navy transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:ring-offset-2 rounded"
              aria-label={`Call ${office.city} office at ${office.phone}`}
            >
              {office.phone}
            </a>
          </div>
        )}
        
        {office.email && (
          <div className="flex items-center gap-3">
            <svg 
              className="w-5 h-5 text-text-light flex-shrink-0" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <a 
              href={`mailto:${office.email}`}
              className="body-sm text-text-medium hover:text-primary-navy transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy focus:ring-offset-2 rounded"
              aria-label={`Email ${office.city} office at ${office.email}`}
            >
              {office.email}
            </a>
          </div>
        )}
      </address>
    </Card>
  );
};